import { useEffect, useMemo, useState } from 'react';
import { PageHeader } from '@ant-design/pro-components';
import { Card, Col, DatePicker, Row, Select, Spin, Statistic, Table, Empty } from 'antd';
import { Content } from 'antd/es/layout/layout';
import DefaultLayout from '@src/components/DefaultLayout';
import { getSalesSummary, SalesSummaryDto } from '@src/apis/sales';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import dayjs, { Dayjs } from 'dayjs';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const { RangePicker } = DatePicker;

const PLATFORM_LABELS: Record<string, string> = {
  payhere: '페이히어',
  naver_booking: '네이버 예약',
  naverpay: '네이버페이',
  hyundai: '현대백화점',
  dsp: 'DSP',
};

const STORE_PLATFORMS: Record<string, string[]> = {
  '성수': ['payhere', 'naver_booking', 'naverpay', 'hyundai', 'dsp'],
  '청주': ['hyundai'],
};

const STORE_COLORS: Record<string, string> = {
  '성수': '#1677ff',
  '청주': '#52c41a',
};

function formatKRW(value: number) {
  return value.toLocaleString('ko-KR') + '원';
}

export default function Dashboard() {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs(),
  ]);
  const [storeFilter, setStoreFilter] = useState<string | undefined>();
  const [platformFilter, setPlatformFilter] = useState<string | undefined>();
  const [data, setData] = useState<SalesSummaryDto | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getSalesSummary({
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        store: storeFilter,
        platform: platformFilter,
      });
      setData(result);
    } catch (e) {
      console.error('매출 데이터 조회 실패', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, storeFilter, platformFilter]);

  const dailyChartData = useMemo(() => {
    if (!data?.daily) return null;
    const sorted = [...data.daily].sort((a, b) => a.saleDate.localeCompare(b.saleDate));
    return {
      labels: sorted.map((d) => dayjs(d.saleDate).format('MM/DD')),
      datasets: [
        {
          label: '총 매출',
          data: sorted.map((d) => d.totalAmount),
          borderColor: '#1677ff',
          backgroundColor: 'rgba(22, 119, 255, 0.1)',
          fill: true,
          tension: 0.3,
        },
        {
          label: '순 이익',
          data: sorted.map((d) => d.totalNetAmount),
          borderColor: '#52c41a',
          backgroundColor: 'rgba(82, 196, 26, 0.1)',
          fill: true,
          tension: 0.3,
        },
      ],
    };
  }, [data]);

  const storeChartData = useMemo(() => {
    if (!data?.byStore) return null;
    return {
      labels: data.byStore.map((s) => s.store),
      datasets: [
        {
          label: '총 매출',
          data: data.byStore.map((s) => s.totalAmount),
          backgroundColor: data.byStore.map((s) => STORE_COLORS[s.store] || '#8c8c8c'),
        },
        {
          label: '순 이익',
          data: data.byStore.map((s) => s.totalNetAmount),
          backgroundColor: data.byStore.map((s) =>
            s.store === '성수' ? 'rgba(22, 119, 255, 0.5)' : 'rgba(82, 196, 26, 0.5)'
          ),
        },
      ],
    };
  }, [data]);

  const platformChartData = useMemo(() => {
    if (!data?.byPlatform) return null;
    const colors = ['#1677ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1'];
    return {
      labels: data.byPlatform.map((p) => PLATFORM_LABELS[p.platform] || p.platform),
      datasets: [
        {
          label: '총 매출',
          data: data.byPlatform.map((p) => p.totalAmount),
          backgroundColor: data.byPlatform.map((_, i) => colors[i % colors.length]),
        },
      ],
    };
  }, [data]);

  const tableColumns = [
    {
      title: '날짜',
      dataIndex: 'saleDate',
      key: 'saleDate',
      render: (v: string) => dayjs(v).format('YYYY-MM-DD'),
    },
    {
      title: '총 매출',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (v: number) => formatKRW(v),
      sorter: (a: any, b: any) => a.totalAmount - b.totalAmount,
    },
    {
      title: '순 이익',
      dataIndex: 'totalNetAmount',
      key: 'totalNetAmount',
      render: (v: number) => formatKRW(v),
    },
    {
      title: '건수',
      dataIndex: 'totalCount',
      key: 'totalCount',
    },
  ];

  const expandedRowRender = (record: any) => {
    const subColumns = [
      { title: '플랫폼', dataIndex: 'platform', key: 'platform', render: (v: string) => PLATFORM_LABELS[v] || v },
      { title: '매장', dataIndex: 'store', key: 'store' },
      { title: '매출', dataIndex: 'amount', key: 'amount', render: (v: number) => formatKRW(v) },
      { title: '순이익', dataIndex: 'netAmount', key: 'netAmount', render: (v: number | null) => v != null ? formatKRW(v) : '-' },
      { title: '수수료', dataIndex: 'commission', key: 'commission', render: (v: number | null) => v != null ? formatKRW(v) : '-' },
      { title: '건수', dataIndex: 'count', key: 'count', render: (v: number | null) => v ?? '-' },
    ];
    return <Table columns={subColumns} dataSource={record.records} pagination={false} rowKey="id" size="small" />;
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${formatKRW(context.parsed.y)}`,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (value: any) => (value / 10000).toLocaleString() + '만',
        },
      },
    },
  };

  return (
    <DefaultLayout>
      <PageHeader
        title="매출 대시보드"
        subTitle="매장별/플랫폼별 매출 현황을 확인할 수 있습니다."
        style={{ margin: '16px 20px 0px 20px' }}
      />
      <Content style={{ padding: '0 24px 24px', overflow: 'auto' }}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates?.[0] && dates?.[1]) setDateRange([dates[0], dates[1]]);
              }}
            />
          </Col>
          <Col>
            <Select
              style={{ width: 120 }}
              value={storeFilter ?? 'all'}
              onChange={(v) => {
                setStoreFilter(v === 'all' ? undefined : v);
                setPlatformFilter(undefined);
              }}
              options={[
                { label: '전체', value: 'all' },
                { label: '성수', value: '성수' },
                { label: '청주', value: '청주' },
              ]}
            />
          </Col>
          <Col>
            <Select
              placeholder="플랫폼 필터"
              allowClear
              style={{ width: 150 }}
              value={platformFilter}
              onChange={setPlatformFilter}
              options={
                (storeFilter ? STORE_PLATFORMS[storeFilter] : Object.keys(PLATFORM_LABELS))
                  .map((key) => ({ label: PLATFORM_LABELS[key] || key, value: key }))
              }
            />
          </Col>
        </Row>

        <Spin spinning={loading}>
          {data ? (
            <>
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col span={8}>
                  <Card>
                    <Statistic title="총 매출" value={data.totalAmount} suffix="원" />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card>
                    <Statistic title="순 이익" value={data.totalNetAmount} suffix="원" />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card>
                    <Statistic title="총 건수" value={data.totalCount} suffix="건" />
                  </Card>
                </Col>
              </Row>

              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col span={24}>
                  <Card title="일별 매출 추이">
                    <div style={{ height: 280 }}>
                      {dailyChartData ? (
                        <Line data={dailyChartData} options={chartOptions} />
                      ) : (
                        <Empty />
                      )}
                    </div>
                  </Card>
                </Col>
              </Row>

              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col span={12}>
                  <Card title="매장별 매출">
                    <div style={{ height: 220 }}>
                      {storeChartData ? (
                        <Bar data={storeChartData} options={chartOptions} />
                      ) : (
                        <Empty />
                      )}
                    </div>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="플랫폼별 매출">
                    <div style={{ height: 220 }}>
                      {platformChartData ? (
                        <Bar data={platformChartData} options={chartOptions} />
                      ) : (
                        <Empty />
                      )}
                    </div>
                  </Card>
                </Col>
              </Row>

              <Card title="일별 매출 상세">
                <Table
                  columns={tableColumns}
                  dataSource={data.daily}
                  rowKey="saleDate"
                  expandable={{ expandedRowRender }}
                  pagination={{ pageSize: 15 }}
                />
              </Card>
            </>
          ) : (
            <Empty description="데이터가 없습니다." />
          )}
        </Spin>
      </Content>
    </DefaultLayout>
  );
}
